import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminWinners, publishWinner, unpublishWinner, deleteAdminWinner, validateTestimonial, createManualWinner } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Trophy, Sparkles, Upload, X, Send, Eye, EyeOff, Trash2, ImagePlus, Camera, Plus,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const inputCls = 'rounded-xl bg-white !text-rolex-green';
const taCls = 'w-full bg-white border border-[hsl(var(--gold)/0.3)] rounded-xl p-3 text-sm resize-none !text-rolex-green focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold)/0.4)]';

export default function AdminWinners() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const [publishDialog, setPublishDialog] = useState({ open: false, winner: null });
  const [photos, setPhotos] = useState([]);
  const [testimonial, setTestimonial] = useState('');
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const fileRef = useRef(null);
  const [testiDialog, setTestiDialog] = useState({ open: false, winner: null });
  const [testiEdit, setTestiEdit] = useState('');
  const [validating, setValidating] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ user_name: '', raffle_title: '', product_value: '', winning_ticket_code: '', testimonial: '', drawn_at: '' });
  const [manualPhotos, setManualPhotos] = useState([]);
  const [manualUploading, setManualUploading] = useState(false);
  const [creatingManual, setCreatingManual] = useState(false);
  const manualFileRef = useRef(null);

  const handleUploadManualPhotos = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setManualUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
      const token = localStorage.getItem('winluxor_token');
      const res = await axios.post(`${API_BASE}/api/upload/multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const newUrls = (res.data.urls || []).map(u => `${API_BASE}${u}`);
      setManualPhotos(prev => [...prev, ...newUrls]);
    } catch { toast.error(t('toastUploadFailed')); }
    finally { setManualUploading(false); if (manualFileRef.current) manualFileRef.current.value = ''; }
  };

  const openManual = () => {
    setManualForm({ user_name: '', raffle_title: '', product_value: '', winning_ticket_code: '', testimonial: '', drawn_at: '' });
    setManualPhotos([]);
    setManualOpen(true);
  };

  const handleCreateManual = async () => {
    setCreatingManual(true);
    try {
      await createManualWinner({ ...manualForm, photos: manualPhotos });
      toast.success(t('winnerCreated') || '🏆 Winner added');
      setManualOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t('toastFailed')); }
    finally { setCreatingManual(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    load();
  }, [user, isAdmin, navigate, authLoading]);

  const load = async () => {
    try { const res = await getAdminWinners(); setWinners(res.data.winners || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const pendingWinners = winners.filter((w) => w.status === 'pending' || !w.status);
  const publishedWinners = winners.filter((w) => w.status === 'published');

  const openPublish = (w) => {
    setPublishDialog({ open: true, winner: w });
    setPhotos(w.photos || []);
    setTestimonial(w.testimonial || '');
  };

  const handleUploadPhotos = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
      const token = localStorage.getItem('winluxor_token');
      const res = await axios.post(`${API_BASE}/api/upload/multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const newUrls = (res.data.urls || []).map(u => `${API_BASE}${u}`);
      setPhotos(prev => [...prev, ...newUrls]);
      toast.success(`${newUrls.length} photo(s) uploaded`);
    } catch { toast.error(t('toastUploadFailed')); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const handlePublish = async () => {
    if (!publishDialog.winner) return;
    setPublishing(true);
    try {
      await publishWinner(publishDialog.winner._id, { photos, testimonial });
      // Mark as no longer using default photos if user added custom photos
      toast.success(t('winnerUpdated') || '✨ Winner updated');
      setPublishDialog({ open: false, winner: null });
      setPhotos([]);
      setTestimonial('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t('toastFailed')); }
    finally { setPublishing(false); }
  };

  const handleUnpublish = async (id) => {
    if (!window.confirm(t('confirmUnpublishWinner'))) return;
    try { await unpublishWinner(id); toast.success(t('winnerUnpublished')); load(); }
    catch { toast.error(t('toastFailed')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDeleteWinner'))) return;
    try { await deleteAdminWinner(id); toast.success(t('winnerDeletedReopen')); load(); }
    catch { toast.error(t('toastFailed')); }
  };

  const openTestiDialog = (w) => {
    setTestiDialog({ open: true, winner: w });
    setTestiEdit(w.user_testimonial || '');
  };

  const handleValidate = async (action) => {
    if (!testiDialog.winner) return;
    setValidating(true);
    try {
      await validateTestimonial(testiDialog.winner._id, action, testiEdit);
      toast.success(action === 'approve' ? (t('testiApproved') || '✓ Testimonial approved') : (t('testiRejected') || 'Testimonial rejected (user notified)'));
      setTestiDialog({ open: false, winner: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('toastFailed'));
    } finally { setValidating(false); }
  };

  if (authLoading || !user || !isAdmin) return null;

  const renderWinnerCard = (w) => (
    <motion.div key={w._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-cream border border-[hsl(var(--gold)/0.30)] overflow-hidden gold-glow">
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gold-gradient flex items-center justify-center shrink-0 shadow">
            <Trophy className="w-7 h-7 text-rolex-green" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {w.status === 'published' ? (
                <Badge className="bg-green-100 text-green-700 border-green-300 gap-1 text-[10px]">✨ {t('public') || 'PUBLIC'}</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 gap-1 text-[10px]">⏳ {t('hidden') || 'HIDDEN'}</Badge>
              )}
              {w.is_using_default_photos && (
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 gap-1 text-[10px]" title="Using default product photos. Click 'Edit photos' to add real handover photos.">
                  📷 {t('defaultPhotos') || 'Default photos'}
                </Badge>
              )}
              <Badge variant="outline" className="text-[10px]">£{w.product_value?.toLocaleString()}</Badge>
            </div>
            <h3 className="font-display text-lg font-bold text-rolex-green leading-tight">{w.user_name}</h3>
            <p className="text-xs text-rolex-green/65">{w.user_email}</p>
            <p className="text-sm text-rolex-green/80 mt-1">{t('won') || 'Won'}: <span className="font-semibold">{w.raffle_title}</span></p>
            <p className="text-[11px] text-rolex-green/50 mt-1">
              {t('designatedAt') || 'Designated'}: {w.designated_at ? new Date(w.designated_at).toLocaleString() : '—'}
              {w.published_at && <> · {t('publishedAt') || 'Published'}: {new Date(w.published_at).toLocaleString()}</>}
            </p>
            {w.testimonial_status === 'pending_admin' && w.user_testimonial && (
              <div className="mt-3 p-3 rounded-xl bg-amber-50 border-2 border-amber-300">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-lg">📝</span>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800">{t('testimonialPending') || 'Testimonial pending review'}</p>
                    <p className="text-xs text-amber-900 italic mt-1 leading-relaxed">{w.user_testimonial}</p>
                  </div>
                </div>
                <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-lg gap-1.5 text-xs font-bold" onClick={() => openTestiDialog(w)}>
                  ✓ {t('reviewTestimonial') || 'Review & approve'}
                </Button>
              </div>
            )}
            {w.testimonial && w.testimonial_status === 'approved' && (
              <p className="text-xs text-rolex-green/70 italic mt-2 border-l-2 border-green-500 pl-3">{w.testimonial}</p>
            )}
            {w.photos && w.photos.length > 0 && (
              <div className="flex gap-1.5 mt-3 flex-wrap">
                {w.photos.slice(0, 4).map((p, i) => (
                  <img key={i} src={p} alt="" className="w-12 h-12 rounded-lg object-cover border border-[hsl(var(--gold)/0.3)]" />
                ))}
                {w.photos.length > 4 && <span className="text-xs text-rolex-green/50 self-center">+{w.photos.length - 4}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <Button size="sm" className="btn-gold rounded-lg gap-1.5 text-xs font-bold flex-1" onClick={() => openPublish(w)} data-testid={`edit-${w._id}`}>
            <Camera className="w-3.5 h-3.5" /> {t('editHandoverPhotos') || 'Edit photos / message'}
          </Button>
          {w.status === 'published' && (
            <Button size="sm" variant="outline" className="rounded-lg gap-1.5 text-xs" onClick={() => handleUnpublish(w._id)} title={t('hideFromPublic') || 'Hide from public'}>
              <EyeOff className="w-3.5 h-3.5" /> {t('hide') || 'Hide'}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="rounded-lg gap-1.5 text-xs !text-red-600" onClick={() => handleDelete(w._id)}>
            <Trash2 className="w-3.5 h-3.5" /> {t('delete') || 'Delete'}
          </Button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="flex min-h-screen bg-rolex-green" style={{ color: 'hsl(43 38% 92%)' }} data-testid="admin-winners">
      <AdminSidebar />
      <main className="flex-1 px-6 lg:px-10 py-10">
        <div className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="gold-line mb-3"><Sparkles className="w-3 h-3" /> Hall of Champions</p>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight text-cream">
              {t('winners')}
            </h1>
            <p className="text-cream/60 mt-2 text-sm max-w-2xl">
              {t('winnersAdminHelp') || 'Manage all designated winners. Publish them once you’ve handed over the prize and have the photos.'}
            </p>
          </div>
          <Button className="btn-gold rounded-xl gap-2 font-bold shrink-0" onClick={openManual} data-testid="create-manual-winner-btn">
            <Plus className="w-4 h-4" /> {t('addManualWinner') || 'Ajouter un gagnant'}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[hsl(var(--rolex-green-elev)/0.5)] backdrop-blur-sm border border-[hsl(var(--gold)/0.20)] mb-5 p-1.5 h-auto">
            <TabsTrigger value="all" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70">
              🏆 {t('allWinners') || 'All Winners'} ({winners.length})
            </TabsTrigger>
            <TabsTrigger value="published" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70">
              ✨ {t('public') || 'Public'} ({publishedWinners.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70">
              ⏳ {t('hidden') || 'Hidden'} ({pendingWinners.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {loading ? (
              <div className="text-center py-10 text-cream/60 text-sm">Loading...</div>
            ) : winners.length === 0 ? (
              <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]">
                <CardContent className="p-10 text-center">
                  <Trophy className="w-12 h-12 text-gold/50 mx-auto mb-3" />
                  <p className="font-semibold text-rolex-green">{t('noWinnersYet') || 'No winners yet'}</p>
                  <p className="text-xs text-rolex-green/60 mt-1">{t('noPendingWinnersHelp') || 'Designate a winner from the Raffles page first.'}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {winners.map(renderWinnerCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="pending">
            {loading ? (
              <div className="text-center py-10 text-cream/60 text-sm">Loading...</div>
            ) : pendingWinners.length === 0 ? (
              <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]">
                <CardContent className="p-10 text-center">
                  <Trophy className="w-12 h-12 text-gold/50 mx-auto mb-3" />
                  <p className="font-semibold text-rolex-green">{t('noHiddenWinners') || 'No hidden winners'}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingWinners.map(renderWinnerCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="published">
            {loading ? (
              <div className="text-center py-10 text-cream/60 text-sm">Loading...</div>
            ) : publishedWinners.length === 0 ? (
              <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]">
                <CardContent className="p-10 text-center">
                  <Trophy className="w-12 h-12 text-gold/50 mx-auto mb-3" />
                  <p className="font-semibold text-rolex-green">{t('noPublishedWinners') || 'No public winners yet'}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {publishedWinners.map(renderWinnerCard)}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Publish Dialog */}
        <Dialog open={publishDialog.open} onOpenChange={(o) => !o && setPublishDialog({ open: false, winner: null })}>
          <DialogContent className="bg-white border-border max-w-xl max-h-[90vh] overflow-y-auto" style={{ color: '#0B3D2E' }}>
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2 !text-rolex-green">
                <Camera className="w-5 h-5 text-gold" /> {t('publishWinnerDialog') || 'Publish winner with photos'}
              </DialogTitle>
              <DialogDescription className="!text-rolex-green/70 text-sm pt-1">
                {publishDialog.winner && (
                  <>
                    <strong>{publishDialog.winner.user_name}</strong> — {publishDialog.winner.raffle_title}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-3">
              <div className="space-y-2">
                <Label className="!text-rolex-green text-sm font-semibold">
                  {t('photosLabel') || 'Photos of the handover'} ({photos.length})
                </Label>
                <p className="text-xs !text-rolex-green/65">{t('photosHelp') || 'Upload 1 to 10 photos showing the winner with their prize.'}</p>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={url} alt="" className="w-full h-24 object-cover rounded-lg border border-[hsl(var(--gold)/0.3)]" />
                        <button onClick={() => removePhoto(idx)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input type="file" ref={fileRef} multiple accept="image/*" onChange={handleUploadPhotos} className="hidden" />
                <Button type="button" variant="outline" className="w-full gap-2 rounded-xl" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Upload className="w-4 h-4" /> {uploading ? (t('uploading') || 'Uploading...') : (t('uploadPhotos') || 'Upload photos')}
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="!text-rolex-green text-sm font-semibold">{t('testimonialLabel') || 'Testimonial / message (optional)'}</Label>
                <textarea
                  value={testimonial}
                  onChange={(e) => setTestimonial(e.target.value)}
                  placeholder="e.g. 'I never thought I'd actually win, this Rolex is a dream come true!'"
                  className={taCls + ' h-24'}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPublishDialog({ open: false, winner: null })} className="rounded-xl">{t('cancel')}</Button>
              <Button className="btn-gold rounded-xl gap-1 font-bold" onClick={handlePublish} disabled={publishing || photos.length === 0}>
                <Send className="w-4 h-4" /> {publishing ? (t('publishing') || 'Publishing...') : (t('publishNow') || 'Publish now')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Manual Winner Dialog */}
        <Dialog open={manualOpen} onOpenChange={setManualOpen}>
          <DialogContent className="bg-white border-border max-w-xl max-h-[90vh] overflow-y-auto" style={{ color: '#0B3D2E' }} data-testid="manual-winner-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2 !text-rolex-green">
                <Trophy className="w-5 h-5 text-gold" /> {t('addManualWinner') || 'Ajouter un gagnant'}
              </DialogTitle>
              <DialogDescription className="!text-rolex-green/70 text-sm pt-1">
                {t('manualWinnerHelp') || 'Pour un gagnant hors site (réseaux sociaux, autres lots). Tous les champs sont optionnels.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="!text-rolex-green text-sm font-semibold">{t('winnerName') || 'Nom du gagnant'}</Label>
                  <Input value={manualForm.user_name} onChange={(e) => setManualForm({ ...manualForm, user_name: e.target.value })} placeholder="Ex : Marie D." className={inputCls} data-testid="manual-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="!text-rolex-green text-sm font-semibold">{t('prizeWon') || 'Lot gagné'}</Label>
                  <Input value={manualForm.raffle_title} onChange={(e) => setManualForm({ ...manualForm, raffle_title: e.target.value })} placeholder="Ex : Rolex Submariner" className={inputCls} data-testid="manual-prize" />
                </div>
                <div className="space-y-1.5">
                  <Label className="!text-rolex-green text-sm font-semibold">{t('value') || 'Valeur'} (£)</Label>
                  <Input type="number" value={manualForm.product_value} onChange={(e) => setManualForm({ ...manualForm, product_value: e.target.value })} placeholder="9500" className={inputCls} data-testid="manual-value" />
                </div>
                <div className="space-y-1.5">
                  <Label className="!text-rolex-green text-sm font-semibold">{t('winningTicket') || 'N° de ticket'}</Label>
                  <Input value={manualForm.winning_ticket_code} onChange={(e) => setManualForm({ ...manualForm, winning_ticket_code: e.target.value })} placeholder="775-03" className={inputCls} data-testid="manual-ticket" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="!text-rolex-green text-sm font-semibold">{t('drawnOn') || 'Date'}</Label>
                  <Input type="date" value={manualForm.drawn_at} onChange={(e) => setManualForm({ ...manualForm, drawn_at: e.target.value })} className={inputCls} data-testid="manual-date" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="!text-rolex-green text-sm font-semibold">{t('photosLabel') || 'Photos'} ({manualPhotos.length})</Label>
                {manualPhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {manualPhotos.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={url} alt="" className="w-full h-24 object-cover rounded-lg border border-[hsl(var(--gold)/0.3)]" />
                        <button onClick={() => setManualPhotos(prev => prev.filter((_, i) => i !== idx))} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input type="file" ref={manualFileRef} multiple accept="image/*" onChange={handleUploadManualPhotos} className="hidden" />
                <Button type="button" variant="outline" className="w-full gap-2 rounded-xl" onClick={() => manualFileRef.current?.click()} disabled={manualUploading} data-testid="manual-upload-btn">
                  <Upload className="w-4 h-4" /> {manualUploading ? (t('uploading') || 'Uploading...') : (t('uploadPhotos') || 'Upload photos')}
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="!text-rolex-green text-sm font-semibold">{t('testimonialLabel') || 'Témoignage / message (optionnel)'}</Label>
                <textarea value={manualForm.testimonial} onChange={(e) => setManualForm({ ...manualForm, testimonial: e.target.value })} placeholder="Ex : Merci WINLUXOR, je n'y croyais pas !" className={taCls + ' h-20'} data-testid="manual-testimonial" />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-xl">{t('cancel')}</Button>
              <Button className="btn-gold rounded-xl gap-1 font-bold" onClick={handleCreateManual} disabled={creatingManual} data-testid="manual-create-confirm">
                <Plus className="w-4 h-4" /> {creatingManual ? (t('saving') || 'Saving...') : (t('addWinner') || 'Ajouter le gagnant')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Testimonial Validation Dialog */}
        <Dialog open={testiDialog.open} onOpenChange={(o) => !o && setTestiDialog({ open: false, winner: null })}>
          <DialogContent className="bg-white border-border max-w-lg" style={{ color: '#0B3D2E' }}>
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2 !text-rolex-green">
                📝 {t('reviewTestimonialTitle') || 'Review testimonial'}
              </DialogTitle>
              <DialogDescription className="!text-rolex-green/70 text-sm pt-1">
                {testiDialog.winner && (<><strong>{testiDialog.winner.user_name}</strong> — {testiDialog.winner.raffle_title}</>)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                💡 {t('reviewTestimonialHelp') || 'You can edit the text below to fix typos before approving. The user will be notified once approved.'}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold !text-rolex-green">{t('originalText') || 'Original text submitted by user'}</Label>
                <p className="text-xs text-rolex-green/70 italic p-3 bg-[hsl(43_30%_94%)] rounded-lg border border-[hsl(var(--gold)/0.2)]">
                  {testiDialog.winner?.user_testimonial || '—'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold !text-rolex-green">{t('finalText') || 'Final text (editable)'}</Label>
                <textarea
                  value={testiEdit}
                  onChange={(e) => setTestiEdit(e.target.value.slice(0, 1000))}
                  className="w-full bg-white border border-[hsl(var(--gold)/0.35)] rounded-xl p-3 text-sm resize-none !text-rolex-green focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold)/0.4)] h-24"
                  data-testid="testi-edit"
                />
                <p className="text-[11px] text-rolex-green/55">{testiEdit.length}/1000</p>
              </div>
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" className="rounded-xl" onClick={() => setTestiDialog({ open: false, winner: null })}>
                {t('cancel')}
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-xl" onClick={() => handleValidate('reject')} disabled={validating}>
                ✗ {t('reject') || 'Reject'}
              </Button>
              <Button className="bg-green-600 hover:bg-green-700 text-white rounded-xl gap-1 font-bold" onClick={() => handleValidate('approve')} disabled={validating || testiEdit.trim().length < 5}>
                ✓ {t('approve') || 'Approve & Publish'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
